import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { getAuditLogs, getAuditStats } from "../../store/auditLog/auditLog.action";
import { apiInstanceFetch } from "../../../util/api";

const outcomeBadge = (o) => {
  switch (o) {
    case "credited":
      return { cls: "bg-success", label: "Credited" };
    case "pending":
      return { cls: "bg-warning text-dark", label: "Pending" };
    case "expired":
      return { cls: "bg-secondary", label: "Expired" };
    case "blocked_device_reused":
      return { cls: "bg-danger", label: "Blocked (device reused)" };
    case "credited_no_device":
      return { cls: "bg-warning text-dark", label: "Credited (no device)" };
    default:
      return { cls: "bg-secondary", label: o || "-" };
  }
};

// Friendly labels for the referee's coin-earning sources (the timeline log).
const sourceLabel = (s) => {
  switch (s) {
    case "ad_view":
      return "Watched ad";
    case "daily_checkin":
      return "Daily check-in";
    case "daily_spin":
      return "Spin wheel";
    case "video_watch":
      return "Watched video";
    case "product_share":
      return "Shared product";
    case "review_submit":
      return "Submitted review";
    case "first_purchase_bonus":
      return "First purchase";
    default:
      return s || "-";
  }
};

const StatCard = ({ label, value, color }) => (
  <div className="col-6 col-md-3 mb-3">
    <div
      style={{
        background: "#FFFDF7",
        border: "1px solid #E8E1D3",
        borderRadius: "12px",
        padding: "16px",
      }}
    >
      <div style={{ fontSize: "24px", fontWeight: 700, color: color || "#1A1614" }}>
        {value ?? 0}
      </div>
      <div style={{ fontSize: "12px", color: "#8A857F" }}>{label}</div>
    </div>
  </div>
);

// Referee progress toward the activation threshold (e.g. 140 / 200) with a bar.
// Clickable → opens the coin timeline popup.
const ProgressCell = ({ row, onClick }) => {
  const coins = row?.refereeCoins ?? 0;
  const threshold = row?.referralThreshold ?? 200;
  const status = row?.referralStatus;
  const active = status === "vested" || (!status && row?.outcome === "credited");
  const expired = status === "expired";
  const pct = active ? 100 : Math.min(100, Math.round((coins / Math.max(1, threshold)) * 100));
  const barColor = active ? "#16A34A" : expired ? "#9CA3AF" : "#D4A129";
  return (
    <div
      style={{ cursor: "pointer", minWidth: "104px" }}
      onClick={onClick}
      title="View coin timeline"
    >
      <div style={{ fontSize: "12px", fontWeight: 700, color: active ? "#16A34A" : "#1A1614" }}>
        {coins}
        <span style={{ color: "#8A857F", fontWeight: 400 }}> / {threshold}</span>
        {active ? <span style={{ color: "#16A34A" }}> ✓</span> : null}
      </div>
      <div
        style={{
          height: "5px",
          background: "#F0EADB",
          borderRadius: "3px",
          marginTop: "3px",
          overflow: "hidden",
        }}
      >
        <div style={{ height: "100%", width: `${pct}%`, background: barColor }}></div>
      </div>
    </div>
  );
};

const ActivatedCell = ({ row }) => {
  const status = row?.referralStatus;
  if (status === "vested" && row?.activatedAt) {
    return (
      <span style={{ color: "#16A34A", fontWeight: 600, fontSize: "12px" }}>
        {dayjs(row.activatedAt).format("DD/MM/YY")}
        <br />
        <span style={{ fontWeight: 400 }}>{dayjs(row.activatedAt).format("HH:mm")}</span>
      </span>
    );
  }
  if (status === "expired") return <span style={{ color: "#9CA3AF", fontSize: "12px" }}>Expired</span>;
  if (status === "pending")
    return <span style={{ color: "#D4A129", fontSize: "12px" }}>Waiting…</span>;
  // Legacy rows (pre-deferral) that were credited instantly at signup.
  if (row?.outcome === "credited")
    return (
      <span style={{ color: "#16A34A", fontSize: "12px" }}>
        {dayjs(row?.createdAt).format("DD/MM/YY")}
      </span>
    );
  return <span style={{ color: "#8A857F" }}>—</span>;
};

const AuditLog = () => {
  const dispatch = useDispatch();
  const { auditLogs, total, stats } = useSelector((state) => state.auditLog);

  const [outcome, setOutcome] = useState("");
  const [search, setSearch] = useState("");
  // Row whose device details popup is open (null = closed).
  const [deviceInfo, setDeviceInfo] = useState(null);
  // Coin timeline popup: { row, loading, data } (null = closed).
  const [timeline, setTimeline] = useState(null);
  const [page, setPage] = useState(1);
  const limit = 25;

  useEffect(() => {
    dispatch(getAuditStats());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAuditLogs({ outcome, search, start: page, limit }));
  }, [dispatch, outcome, search, page]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / limit));

  const openTimeline = (row) => {
    if (!row?.actorUserId) return;
    setTimeline({ row, loading: true, data: null });
    apiInstanceFetch
      .get(`auditLog/refereeTimeline?userId=${row.actorUserId}`)
      .then((res) => setTimeline({ row, loading: false, data: res?.data || null }))
      .catch(() => setTimeline({ row, loading: false, data: null }));
  };

  return (
    <div className="mainCategory">
      <Title name="Coins Logs" />

      {/* Stat cards */}
      <div className="row mt-3">
        <StatCard label="Referral Signups" value={stats?.totalReferralSignups} />
        <StatCard label="Pending activation" value={stats?.pending} color="#D4A129" />
        <StatCard label="Activated (rewarded)" value={stats?.credited} color="#16A34A" />
        <StatCard
          label="Blocked (device reused)"
          value={stats?.blockedDeviceReused}
          color="#DC2626"
        />
        <StatCard label="Coins Awarded" value={stats?.coinsAwarded} color="#D4A129" />
      </div>

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-12 col-md-4 mb-2">
          <select
            className="form-control"
            value={outcome}
            onChange={(e) => {
              setOutcome(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All outcomes</option>
            <option value="pending">Pending activation</option>
            <option value="credited">Activated (credited)</option>
            <option value="expired">Expired</option>
            <option value="blocked_device_reused">Blocked (device reused)</option>
            <option value="credited_no_device">Credited (no device)</option>
          </select>
        </div>
        <div className="col-12 col-md-5 mb-2">
          <input
            type="text"
            className="form-control"
            placeholder="Search name, email, code, device id..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="col-12 col-md-3 mb-2 d-flex align-items-center">
          <span style={{ color: "#8A857F" }}>
            Total: <strong>{total}</strong>
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="customTable">
        <div className="primeMain">
          <table width="100%" className="primeTable text-center">
            <thead className="sticky-top" style={{ top: "-1px", zIndex: 1 }}>
              <tr>
                <th className="fw-bold py-3">No</th>
                <th className="fw-bold py-3">Date</th>
                <th className="fw-bold py-3">New User</th>
                <th className="fw-bold py-3">Referrer</th>
                <th className="fw-bold py-3">Code</th>
                <th className="fw-bold py-3">Referral Coins</th>
                <th className="fw-bold py-3">Activated</th>
                <th className="fw-bold py-3">Device</th>
                <th className="fw-bold py-3">Outcome</th>
                <th className="fw-bold py-3">Coins</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs && auditLogs.length > 0 ? (
                auditLogs.map((row, index) => {
                  const b = outcomeBadge(row?.outcome);
                  return (
                    <tr key={row?._id || index}>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td style={{ fontSize: "12px" }}>
                        {dayjs(row?.createdAt).format("DD/MM/YY")}
                        <br />
                        <span style={{ color: "#8A857F" }}>
                          {dayjs(row?.createdAt).format("HH:mm")}
                        </span>
                      </td>
                      <td style={{ fontSize: "12px", textAlign: "left" }}>
                        <strong>{row?.actorName || "-"}</strong>
                        <br />
                        <span style={{ color: "#8A857F" }}>{row?.actorEmail}</span>
                      </td>
                      <td style={{ fontSize: "12px" }}>{row?.beneficiaryName || "-"}</td>
                      <td style={{ fontSize: "12px" }}>{row?.referralCode || "-"}</td>
                      <td>
                        <ProgressCell row={row} onClick={() => openTimeline(row)} />
                      </td>
                      <td>
                        <ActivatedCell row={row} />
                      </td>
                      <td>
                        {row?.deviceId || row?.deviceModel ? (
                          <i
                            className="fa-solid fa-circle-info"
                            title="View device info"
                            style={{ color: "#D4A129", cursor: "pointer", fontSize: "16px" }}
                            onClick={() => setDeviceInfo(row)}
                          ></i>
                        ) : (
                          <span style={{ color: "#8A857F" }}>-</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${b.cls}`}>{b.label}</span>
                      </td>
                      <td style={{ fontWeight: 700, color: "#D4A129" }}>{row?.coins || 0}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} style={{ padding: "30px", color: "#8A857F" }}>
                    No audit logs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 12,
          marginTop: 14,
        }}
      >
        <button
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          style={{
            padding: "7px 14px",
            borderRadius: 8,
            border: "1px solid #E8E1D3",
            background: page <= 1 ? "#F3F0E9" : "#fff",
            color: "#1A1614",
            cursor: page <= 1 ? "default" : "pointer",
            fontSize: 13,
          }}
        >
          Prev
        </button>
        <span style={{ fontSize: 13, color: "#8A857F" }}>
          Page {page} / {totalPages}
        </span>
        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          style={{
            padding: "7px 14px",
            borderRadius: 8,
            border: "1px solid #E8E1D3",
            background: page >= totalPages ? "#F3F0E9" : "#fff",
            color: "#1A1614",
            cursor: page >= totalPages ? "default" : "pointer",
            fontSize: 13,
          }}
        >
          Next
        </button>
      </div>

      {/* Device info popup */}
      {deviceInfo && (
        <div className="mainDialogue fade-in" onClick={() => setDeviceInfo(null)}>
          <div
            className="Dialogue"
            style={{ width: "460px", maxWidth: "95%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dialogueHeader">
              <div className="headerTitle fw-bold">Device Information</div>
              <div className="closeBtn" onClick={() => setDeviceInfo(null)}>
                <i className="fa-solid fa-xmark"></i>
              </div>
            </div>
            <div className="dialogueMain" style={{ padding: "20px" }}>
              <div className="text-dark mb-3">
                <strong>{deviceInfo?.actorName || "-"}</strong>
                <br />
                <span style={{ color: "#8A857F", fontSize: "12px" }}>
                  {deviceInfo?.actorEmail}
                </span>
              </div>
              <InfoRow label="Device Model" value={deviceInfo?.deviceModel || "—"} />
              <InfoRow label="Platform" value={deviceInfo?.platform || "—"} />
              <InfoRow label="Device ID" value={deviceInfo?.deviceId || "—"} breakAll />
              <InfoRow label="IP Address" value={deviceInfo?.ip || "—"} />
              <InfoRow label="Referral Code" value={deviceInfo?.referralCode || "—"} />
              <InfoRow label="Referrer" value={deviceInfo?.beneficiaryName || "—"} />
            </div>
          </div>
        </div>
      )}

      {/* Coin timeline popup */}
      {timeline && (
        <div className="mainDialogue fade-in" onClick={() => setTimeline(null)}>
          <div
            className="Dialogue"
            style={{ width: "520px", maxWidth: "96%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dialogueHeader">
              <div className="headerTitle fw-bold">Referral coin timeline</div>
              <div className="closeBtn" onClick={() => setTimeline(null)}>
                <i className="fa-solid fa-xmark"></i>
              </div>
            </div>
            <div className="dialogueMain" style={{ padding: "20px" }}>
              <div className="text-dark mb-2">
                <strong>{timeline?.row?.actorName || "-"}</strong>
                <br />
                <span style={{ color: "#8A857F", fontSize: "12px" }}>
                  {timeline?.row?.actorEmail}
                </span>
              </div>

              {timeline.loading ? (
                <div style={{ padding: "24px", textAlign: "center", color: "#8A857F" }}>
                  Loading…
                </div>
              ) : timeline.data ? (
                <>
                  {/* Summary */}
                  <div
                    style={{
                      background: "#FFFDF7",
                      border: "1px solid #E8E1D3",
                      borderRadius: "10px",
                      padding: "12px 14px",
                      marginBottom: "14px",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#8A857F", fontSize: "13px" }}>Current coins</span>
                      <span style={{ fontWeight: 700 }}>
                        {timeline.data.currentCoins} / {timeline.data.threshold}
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px" }}>
                      <span style={{ color: "#8A857F", fontSize: "13px" }}>Status</span>
                      <span style={{ fontWeight: 600 }}>
                        {timeline.data.status === "vested"
                          ? "Activated ✓"
                          : timeline.data.status === "expired"
                          ? "Expired"
                          : timeline.data.status === "pending"
                          ? "Waiting for activation"
                          : "—"}
                      </span>
                    </div>
                    {timeline.data.activatedAt ? (
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px" }}>
                        <span style={{ color: "#8A857F", fontSize: "13px" }}>Became active</span>
                        <span style={{ fontWeight: 600, color: "#16A34A" }}>
                          {dayjs(timeline.data.activatedAt).format("DD/MM/YY HH:mm")}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  {/* Per-earn timeline ("every moment") */}
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {timeline.data.timeline && timeline.data.timeline.length > 0 ? (
                      timeline.data.timeline.map((t, i) => {
                        const reached = t.runningTotal >= timeline.data.threshold;
                        return (
                          <div
                            key={i}
                            className="d-flex justify-content-between align-items-center py-2"
                            style={{ borderBottom: "1px solid #F0EADB" }}
                          >
                            <div>
                              <span style={{ fontSize: "13px", fontWeight: 600 }}>
                                {sourceLabel(t.source)}
                              </span>
                              <br />
                              <span style={{ color: "#8A857F", fontSize: "11px" }}>
                                {dayjs(t.at).format("DD/MM/YY HH:mm")}
                              </span>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <span style={{ color: "#16A34A", fontWeight: 600, fontSize: "13px" }}>
                                +{t.amount}
                              </span>
                              <br />
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  color: reached ? "#16A34A" : "#8A857F",
                                }}
                              >
                                {t.runningTotal} total{reached ? " ✓" : ""}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: "16px", textAlign: "center", color: "#8A857F" }}>
                        No qualifying coins earned yet.
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ padding: "20px", textAlign: "center", color: "#DC2626" }}>
                  Could not load timeline.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoRow = ({ label, value, breakAll }) => (
  <div className="d-flex justify-content-between py-2" style={{ borderBottom: "1px solid #F0EADB" }}>
    <span style={{ color: "#8A857F", fontSize: "13px" }}>{label}</span>
    <span
      className="text-dark"
      style={{
        fontSize: "13px",
        fontWeight: 600,
        textAlign: "right",
        maxWidth: "260px",
        wordBreak: breakAll ? "break-all" : "normal",
      }}
    >
      {value}
    </span>
  </div>
);

export default AuditLog;
